/* Common Imports */

import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	setBusinessDashboardCurrentScreenThunk
} from "../../../../../redux-magic/store";

/* Component Imports */

import { Box, Skeleton, styled, useMediaQuery, useTheme } from "@mui/material";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import AiBoostFormOfflineMobileModal from "../../../../modals/AiBoostFormOfflineMobileModal";
import AIBoostFormOfflineModal from "../../../../modals/AiBoostFormOfflineModal";
import CreateNewBroadcastModal from "../../../../page-sections/Broadcasts/CreateNewBroadcastModal";

/* Icon Imports */

import { CampaignOutlined, HourglassTopOutlined, OfflineBoltOutlined, TaskAltOutlined } from "@mui/icons-material";

const ButtonContainer = styled(Box)({
	display: "flex",
	flexDirection: "row",
	width: "100%",
});

const PropertyCardCTA = ({
	loading,
	router,
	published_status,
	dispatch,
	slug,
	title,
	created_by,
	isEditor,
	isBoosted,
	profile_context,
	broadcastData,
	transactionType,
	propertyId,
	business_profile_id,
	session,
	propertyStatus,
	is_recently_broadcasted,
	is_broadcasted_before,
	time_left_to_broadcast_again,
	broadcastFeatureDiscoveryTracker,
	ai_boosts,
}: {
	loading: boolean;
	router: NextRouter;
	published_status: string | undefined;
	dispatch: Function;
	slug: string;
	title: string;
	created_by: any;
	isEditor: boolean | undefined;
	isBoosted: boolean;
	profile_context: StateInterface["profile_context"];
	broadcastData: StateInterface["broadcast_data"] | null | undefined;
	transactionType: string;
	propertyId: ObjectId | string;
	business_profile_id: string | ObjectId | undefined | null;
	session: StateInterface["redux_session"];
	propertyStatus: string;
	is_recently_broadcasted: boolean;
	is_broadcasted_before: boolean;
	time_left_to_broadcast_again: number;
	broadcastFeatureDiscoveryTracker: StateInterface["feature_discovery_tracker"]["broadcast"];
	ai_boosts: StateInterface["ai_boosts"];
}) => {
	const theme = useTheme();
	const isSmallDevice = useMediaQuery(theme.breakpoints.down("sm910"));

	const [openBroadcastModal, setOpenBroadcastModal] = React.useState(false);
	const [openboost, setOpenBoost] = React.useState(false);

	/* Translations */

	const localLang = {
		active_boost:
			router.locale === "ar-AE" ? "التعزيز النشط" : router.locale === "kn-IN" ? "ಸಕ್ರಿಯ ಬೂಸ್ಟ್" : "Active Boost",
		boost_now: router.locale === "ar-AE" ? "تعزيز الآن" : router.locale === "kn-IN" ? "ಈಗ ಬೂಸ್ಟ್ ಮಾಡಿ" : "Boost now",
		in_review: router.locale === "ar-AE" ? "تحت المراجعة" : router.locale === "kn-IN" ? "ಪುನರಾವಲೋಕನದಲ್ಲಿ" : "In review",
		publish_now: router.locale === "ar-AE" ? "نشر الآن" : router.locale === "kn-IN" ? "ಈಗ ಪ್ರಕಟಿಸಿ" : "Publish now",
		published: router.locale === "ar-AE" ? "نشر" : router.locale === "kn-IN" ? "ಪ್ರಕಟಿಸಲಾಗಿದೆ" : "Published",
		broadcast_now:
			router.locale === "ar-AE" ? "البث الآن" : router.locale === "kn-IN" ? "ಈಗ ಪ್ರಸಾರ ಮಾಡಿ" : "Broadcast now",
		broadcast_again:
			router.locale === "ar-AE" ? "بث مرة أخرى" : router.locale === "kn-IN" ? "ಮರು ಪ್ರಸಾರ ಮಾಡಿ" : "Broadcast again",
		broadcast: router.locale === "ar-AE" ? "بث" : router.locale === "kn-IN" ? "ಪ್ರಸಾರ ಮಾಡಿ" : "Broadcast",
	};

	/* Let's check if the user is allowed to boost */

	const canUserBoost: boolean = profile_context
		? profile_context.user_id === (created_by ? (created_by.user_id ? created_by.user_id._id : "") : "")
		: false;

	function formatTimeLeft(ms: number): string {
		if (ms <= 0) return "0M";
		const totalMinutes = Math.ceil(ms / (60 * 1000));
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		// If less than 1 hour, show only minutes
		if (hours === 0) {
			return `${totalMinutes}M`;
		} else {
			// If there are leftover minutes, round up to next hour
			const displayHours = minutes > 0 ? hours + 1 : hours;
			return `${displayHours}H`;
		}
	}

	const translations = {
		en: "Broadcast in:",
		"ar-AE": "يمكنك البث بعد:",
		"kn-IN": "ಪ್ರಸಾರ ಸಮಯ:",
	};

	function getBroadcastMessage(locale: string | undefined, msLeft: number) {
		let langKey: keyof typeof translations = "en";
		if (locale === "ar-AE") langKey = "ar-AE";
		else if (locale === "kn-IN") langKey = "kn-IN";
		const timeStr = formatTimeLeft(msLeft);
		return `${translations[langKey]} ${timeStr}`;
	}

	// Example usage:
	const message = getBroadcastMessage(router.locale, time_left_to_broadcast_again);

	if (loading) {
		return (
			<Skeleton
				animation="wave"
				variant="text"
				width="75%"
				height="auto"
				style={{ marginBottom: "0.5rem" }}
			/>
		);
	} else {
		return isEditor ? (
			published_status === "published" ? (
				isBoosted ? (
					<ButtonContainer>
						{/* Active Boost */}

						<BeegruButton
							flavor="boost"
							variant="contained"
							sx={{
								flex: 1,
								// width: "100%",
								borderRadius: "0rem 0rem 0rem 0.75rem",
								fontSize: "0.875rem",
								"&.MuiButton-contained": {
									background: "#e7dfe8",
									color: "#693A70",
									boxShadow: "none",
									"&:focus": {
										background: "#e7dfe8",
										color: "#693A70",
										boxShadow: "none",
									},
									"&:hover": {
										background: "#e7dfe8",
										color: "#693A70",
										boxShadow: "none",
									},
								},
								/*910*/
								[theme.breakpoints.up("sm910")]: {
									fontSize: "0.95rem",
								},
							}}
							onClick={(event: any) => {
								event.preventDefault();

								dispatch(setBusinessDashboardCurrentScreenThunk("ai-boosts-details"));
								router.push(
									{
										pathname: router.pathname,
										query: {
											...router.query,
											screen: "ai-boosts-details",
											property_id: propertyId.toString(),
										},
									},
									undefined,
									{ shallow: true },
								);
							}}
						>
							<OfflineBoltOutlined
								sx={{
									fontSize: "1.25rem",
									margin: "0rem 0.25rem 0rem 0rem",
								}}
							/>
							{localLang.active_boost}
						</BeegruButton>

						{/* Broadcast now */}

						<BeegruButton
							flavor="broadcast"
							variant="contained"
							disabled={is_recently_broadcasted}
							sx={{
								flex: 1,
								borderRadius: "0rem 0rem 0.75rem 0rem",
								fontSize: "0.875rem",
								// background: "#621B16",
								"&.MuiButton-contained": {
									boxShadow: "none",
								},
								/*910*/
								[theme.breakpoints.up("sm910")]: {
									fontSize: "0.95rem",
								},
							}}
							onClick={(event: any) => {
								event.preventDefault();
								setOpenBroadcastModal(true);
							}}
						>
							<CampaignOutlined
								sx={{
									fontSize: "1.25rem",
									margin: "0rem 0.5rem 0rem 0rem",
								}}
							/>
							{is_recently_broadcasted
								? message
								: is_broadcasted_before
									? localLang.broadcast_again
									: localLang.broadcast_now}
						</BeegruButton>

						{/* Broadcast Modal */}

						<CreateNewBroadcastModal
							source={{ url: slug, component: "listing_card" }}
							router={router}
							setOpen={setOpenBroadcastModal}
							open={openBroadcastModal}
							propertyStatus={propertyStatus}
							session={session}
							profile_context={profile_context}
							business_profile_id={business_profile_id}
							dispatch={dispatch}
							broadcast={broadcastData}
							transactionType={transactionType}
							propertyId={propertyId}
							broadcastFeatureDiscoveryTracker={broadcastFeatureDiscoveryTracker}
						/>
					</ButtonContainer>
				) : (
					/* Broadcast now */

					<>
						<ButtonContainer>
							<BeegruButton
								flavor="broadcast"
								variant="contained"
								disabled={is_recently_broadcasted}
								sx={{
									width: "100%",
									flex: 1,
									borderRadius: "0rem 0rem 0rem 0.75rem",
									fontSize: "0.875rem",
									// background: "#621B16",
									"&.MuiButton-contained": {
										boxShadow: "none",
									},
									/*910*/
									[theme.breakpoints.up("sm910")]: {
										fontSize: "0.95rem",
									},
								}}
								onClick={(event: any) => {
									event.preventDefault();
									setOpenBroadcastModal(true);
									// dispatch(
									// 	SetPropertyBoostModalStateThunk({
									// 		state: true,
									// 		id: slug,
									// 		title: title,
									// 		createdBy: created_by,
									// 		active: isBoosted,
									// 	}),
									// );

									// /* Fetch Boost Data */

									// dispatch(SetPropertyBoostDataStateThunk({ slug: slug }));

									// /* Showing first page since boost is inactive */

									// dispatch(SetPropertyBoostMobileStepStateThunk({ step: 0 }));
								}}
							>
								<CampaignOutlined
									sx={{
										fontSize: "1.25rem",
										margin: "0rem 0.5rem 0rem 0rem",
									}}
								/>
								{is_recently_broadcasted
									? message
									: is_broadcasted_before
										? localLang.broadcast_again
										: localLang.broadcast_now}
							</BeegruButton>

							{/* Boost Now */}

							<BeegruButton
								flavor="boost"
								variant="contained"
								onClick={(event: any) => {
									event.preventDefault();
									setOpenBoost(true);
								}}
								sx={{
									flex: 1,
									borderRadius: "0rem 0rem 0.75rem 0rem",
									fontSize: "0.875rem",
									"&.MuiButton-contained": {
										boxShadow: "none",
									},
									[theme.breakpoints.up("sm910")]: {
										fontSize: "0.95rem",
									},
								}}
							>
								<OfflineBoltOutlined
									sx={{
										fontSize: "1.25rem",
										margin: "0rem 0.5rem 0rem 0rem",
									}}
								/>
								{localLang.boost_now}
							</BeegruButton>
						</ButtonContainer>

						{/* Broadcast Modal */}

						<CreateNewBroadcastModal
							source={{ url: slug, component: "listing_card" }}
							router={router}
							setOpen={setOpenBroadcastModal}
							open={openBroadcastModal}
							propertyStatus={propertyStatus}
							session={session}
							profile_context={profile_context}
							business_profile_id={business_profile_id}
							dispatch={dispatch}
							broadcast={broadcastData}
							transactionType={transactionType}
							propertyId={propertyId}
							broadcastFeatureDiscoveryTracker={broadcastFeatureDiscoveryTracker}
						/>

						{/* Desktop Contact Form Modal */}

						{!isSmallDevice && (
							<AIBoostFormOfflineModal
								source={{ url: slug, component: "listing_card", form_type: "aiboost_offlineform" }}
								open={openboost}
								handleClose={() => setOpenBoost(false)}
								dispatch={dispatch}
								propertyId={propertyId}
							/>
						)}

						{/* Mobile Contact Form Modal */}

						{isSmallDevice && (
							<AiBoostFormOfflineMobileModal
								source={{ url: slug, component: "listing_card", form_type: "aiboost_mobile_offlineform" }}
								open={openboost}
								handleClose={() => setOpenBoost(false)}
								dispatch={dispatch}
								propertyId={propertyId}
							/>
						)}
					</>
				)
			) : published_status === "publish_requested" || published_status === "pending_qc" ? (
				/* In review */

				<BeegruButton
					flavor="primary"
					variant="contained"
					sx={{
						width: "100%",
						borderRadius: "0rem 0rem 0.75rem 0.75rem",
						fontSize: "0.875rem",
						"&.MuiButton-contained": {
							"&.Mui-disabled": {
								"&.Mui-disabled": {
									background: "#feeada",
									color: "#B05911",
									boxShadow: "none",
								},
							},
						},
						/*910*/
						[theme.breakpoints.up("sm910")]: {
							fontSize: "0.95rem",
						},
					}}
					disabled
				>
					<HourglassTopOutlined
						sx={{
							fontSize: "1rem",
							margin: "0rem 0.25rem 0rem 0rem",
						}}
					/>
					{localLang.in_review}
				</BeegruButton>
			) : published_status === "published" && !canUserBoost ? (
				/* Published */
				<>
					<BeegruButton
						flavor="primary"
						variant="contained"
						sx={{
							width: "100%",
							borderRadius: "0rem 0rem 0.75rem 0.75rem",
							fontSize: "0.875rem",
							"&.MuiButton-contained": {
								"&.Mui-disabled": {
									background: "#feeada",
									color: "#B05911",
									boxShadow: "none",
								},
							},
							/*910*/
							[theme.breakpoints.up("sm910")]: {
								fontSize: "0.95rem",
							},
						}}
						disabled
					>
						<TaskAltOutlined
							sx={{
								fontSize: "1.25rem",
								margin: "0rem 0.25rem 0rem 0rem",
							}}
						/>
						{localLang.published}
					</BeegruButton>
				</>
			) : (
				/* Publish now */

				<BeegruButton
					flavor="primary"
					variant="contained"
					sx={{
						width: "100%",
						borderRadius: "0rem 0rem 0.75rem 0.75rem",
						fontSize: "0.875rem",
						"&.MuiButton-contained": {
							boxShadow: "none",
						},
						/*910*/
						[theme.breakpoints.up("sm910")]: {
							fontSize: "0.95rem",
						},
					}}
				>
					{localLang.publish_now}
				</BeegruButton>
			)
		) : null;
	}
};

export default PropertyCardCTA;
